import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Package } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { NAV } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";
import { useScrolled } from "@/hooks/useScrolled";
import BrandsDropdown from "@/components/site/header/BrandsDropdown";
import LangSelector from "@/components/site/header/LangSelector";
import MobileMenu from "@/components/site/header/MobileMenu";
import SampleRequestModal from "@/components/site/SampleRequestModal";

// Kept exported here so existing consumers (Footer.jsx) can keep importing
// `HuplaLogo` from `@/components/site/Header` without any change.
export function HuplaLogo({ className = "h-11", wordmarkColor = "#001F39" }) {
  return (
    <div className={`${className} flex items-center gap-2`} aria-label="Hupla">
      <svg
        viewBox="0 0 74.27 75.18"
        fill="currentColor"
        className="h-full w-auto text-[#25bfcf]"
        aria-hidden
      >
        <path d="M69.09 27.07l-6.28-4.81V13.2C62.8 5.92 56.89 0 49.61 0c-1.49 0-2.96.25-4.37.75l-8.09 2.83L29.06.75C27.64.25 26.17 0 24.69 0a13.12 13.12 0 0 0-9.22 3.76c-2.57 2.5-3.98 5.86-3.98 9.45v9.06L5.18 27.1C1.88 29.62 0 33.44 0 37.59s1.9 7.97 5.2 10.49l6.29 4.8v8.83c0 7.28 5.92 13.2 13.19 13.2 1.49 0 2.96-.25 4.37-.75l8.09-2.83 8.09 2.83c1.41.49 2.88.75 4.37.75 7.28 0 13.19-5.92 13.19-13.2v-8.94l6.19-4.65a13.1 13.1 0 0 0 5.27-10.49 13.08 13.08 0 0 0-5.18-10.54zm-5.62 13.68l-7.27 5.46c-1.63 1.23-2.6 3.15-2.6 5.2V61.7c0 2.29-1.87 4-4 4a3.92 3.92 0 0 1-1.33-.23l-8.98-3.15a6.61 6.61 0 0 0-2.15-.37c-.73 0-1.45.12-2.15.37l-8.98 3.15a3.88 3.88 0 0 1-1.33.23c-2.12 0-4-1.72-4-4V51.54a6.51 6.51 0 0 0-2.56-5.17l-7.36-5.61a4 4 0 0 1 0-6.36l7.37-5.64c1.61-1.23 2.55-3.14 2.55-5.16V13.21c0-2.29 1.87-4 4-4a3.92 3.92 0 0 1 1.33.23l8.98 3.15a6.61 6.61 0 0 0 2.15.37c.73 0 1.45-.12 2.15-.37l8.98-3.15a3.88 3.88 0 0 1 1.33-.23c2.12 0 4 1.72 4 4V23.6c0 2.02.94 3.93 2.55 5.16l7.34 5.62a4.01 4.01 0 0 1-.03 6.38z" />
      </svg>
      <svg
        viewBox="85 22 305.84 53"
        fill="currentColor"
        className="h-[55%] w-auto"
        style={{ color: wordmarkColor }}
        aria-label="HUPLA"
      >
        <path d="M137.89 22.06h-9.26a1.93 1.93 0 0 0-1.93 1.93v13.16l-29.03-.03V23.99a1.93 1.93 0 0 0-1.93-1.93h-9.26a1.94 1.94 0 0 0-1.94 1.93v47.75c0 1.07.86 1.93 1.93 1.93h9.27a1.93 1.93 0 0 0 1.93-1.93V49.86h29.03v21.88a1.93 1.93 0 0 0 1.93 1.93h9.27a1.93 1.93 0 0 0 1.93-1.93V23.99c0-1.07-.87-1.93-1.94-1.93zm62.13 0h-8.8c-1.13 0-2.02.89-2.02 2.03l-.02 23.61c-.02 5.96-.4 10.05-5.35 12.43-4.46 2.14-12.57 2.91-18.43.48-3.06-1.27-5.06-3.23-5.97-5.83-.23-.66-.62-2.47-.67-7.15l-.04-23.5c.01-.57-.19-1.1-.56-1.48a2.02 2.02 0 0 0-1.47-.6h-8.94a2.03 2.03 0 0 0-2.05 2.06l.03 26.19c0 3.66.31 6.49.91 8.41 2.61 8.45 9.7 13.99 19.96 15.6a48.89 48.89 0 0 0 7.5.59c7.83 0 14.82-2 19.67-5.63 4.55-3.41 7.26-8.13 8.03-14.05.29-2.22.43-4.35.4-6.35l-.15-24.79c0-1.14-.89-2.03-2.03-2.03zm112.92 39.96h-35.1V24.04c0-1.09-.89-1.98-1.98-1.98h-9.17c-1.09 0-1.98.89-1.98 1.98V71.7c0 1.09.89 1.98 1.98 1.98h46.2c1.09 0 1.98-.89 1.98-1.97l.05-7.71c0-1.09-.89-1.98-1.98-1.98zm77.71 8.87l-23.47-47.71c-.31-.68-1.01-1.12-1.76-1.12h-21.95c-.75 0-1.44.43-1.74 1.09l-23.36 47.72c-.31.6-.28 1.31.08 1.9.36.57.98.92 1.65.92h10.36c.78 0 1.47-.44 1.75-1.11l5.34-11.23h33.56l5.31 11.17c.3.71 1 1.17 1.78 1.17h10.69a1.93 1.93 0 0 0 1.64-.91c.36-.57.39-1.27.1-1.88zm-48-20.62l7.58-16.36 8.14-.16 7.65 16.52h-23.37zm-82.04-15.93c-.31-2.56-1.23-4.8-2.73-6.67-2.85-3.55-7.6-5.53-13.38-5.57l-35.13-.02a2 2 0 0 0-2.02 2.02v47.49c0 1.19.89 2.09 2.08 2.09h9.04a2.01 2.01 0 0 0 2.01-2.01V54.48l24.01-.06c5.78-.04 10.53-2.02 13.38-5.57 1.65-2.05 2.59-4.54 2.81-7.42l.03-.78c0-.37.02-.74 0-1.09l-.1-4.92-.02-.3zm-12.65 4.91c0 1.99-1.62 3.6-3.6 3.6h-24.25v-9.18h24.25c1.99 0 3.6 1.62 3.6 3.6v1.97z" />
      </svg>
    </div>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT();
  const scrolled = useScrolled(40);
  const isBrandPage = location.pathname.startsWith("/brands/");

  const links = [
    { id: "about", label: t("nav.about"), testId: NAV.linkAbout },
    { id: "sustainability", label: t("nav.sustainability"), testId: NAV.linkAbout + "-sust" },
    { id: "b2b", label: t("nav.b2b"), testId: NAV.linkB2B },
    { id: "contact", label: t("nav.contact"), testId: NAV.linkContact },
  ];

  const goToSection = useCallback(
    (id) => {
      setMobileOpen(false);
      if (location.pathname === "/") {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate(`/#${id}`);
      }
    },
    [location.pathname, navigate]
  );

  const goToBrand = useCallback(
    (slug) => {
      setMobileOpen(false);
      navigate(`/brands/${slug}`);
    },
    [navigate]
  );

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || isBrandPage
          ? "backdrop-blur-xl bg-white/90 border-b border-black/5 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() =>
            location.pathname === "/" ? goToSection("hero") : navigate("/")
          }
          data-testid={NAV.headerLogo}
          className="flex items-center group"
        >
          <HuplaLogo className="h-12 md:h-14" />
        </button>

        <nav className="hidden lg:flex items-center">
          <div className="flex items-center gap-1">
            <BrandsDropdown
              onSelectBrand={goToBrand}
              onBrandsSection={() => goToSection("brands")}
            />
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={l.testId}
                onClick={() => goToSection(l.id)}
                className="relative px-3 py-2 text-sm font-medium text-neutral-800 hover:text-[#25bfcf] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="ml-6 xl:ml-10 flex items-center gap-0">
            <button
              data-testid="header-sample-request"
              onClick={() => setSampleOpen(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-neutral-800 hover:text-[#25bfcf] transition-colors"
            >
              <Package className="w-4 h-4" />
              {t("nav.sample")}
            </button>
            <button
              data-testid="header-distributors-link"
              onClick={() => navigate("/distributors/login")}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium text-neutral-800 hover:text-[#B02224] transition-colors"
            >
              {t("nav.distributors")}
            </button>
            <button
              data-testid={NAV.ctaInquiry}
              onClick={() => goToSection("b2b")}
              className="ml-2 inline-flex items-center gap-2 rounded-full bg-[#001F39] text-white pl-5 pr-2 py-2 text-sm font-medium hover:bg-[#25bfcf] transition-all"
            >
              {t("nav.cta")}
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#25bfcf] text-[#001F39]">
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </button>
            <LangSelector open={langOpen} setOpen={setLangOpen} />
          </div>
        </nav>

        <div className="lg:hidden flex items-center gap-2">
          <LangSelector open={langOpen} setOpen={setLangOpen} />
          <button
            data-testid={NAV.mobileMenuBtn}
            className="p-2 rounded-full bg-black/5"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <MobileMenu
        open={mobileOpen}
        links={links}
        onSectionClick={goToSection}
        onBrandClick={goToBrand}
        onCtaClick={() => goToSection("b2b")}
        onSampleClick={() => { setMobileOpen(false); setSampleOpen(true); }}
      />

      <SampleRequestModal open={sampleOpen} onClose={() => setSampleOpen(false)} />
    </motion.header>
  );
}
